import SpineWrapper from '@Core/Animation/SpineWrapper';
import { SpecialSymbolModifier } from '@Core/Events/EventModifiers/SpecialSymbolModifier';
import { SymbolSpinePool } from '@Core/SpinePool/SymbolSpinePool';
import { CycleStep } from '@Core/SymbolsHighlighter/SymbolsHighlighter.types';
import { TextContainer } from '@Core/TextContainer/TextContainer';
import { TextContainerConfig } from '@Core/TextContainer/TextContainer.types';
import { setContainerScaleToFitSpineSize } from '@Core/Utils/ContainerUtils';
import { getSymbolJackpotLevels, getSymbolRoles, isMultiSymbol } from '@Core/Utils/SymbolUtils';
import { GameStore } from '@GameStore/GameStore';
import { EGameStoreKeys } from '@GameStore/GameStoreSchema';
import { IMultiSymbolFeature } from '@GFTypes/index';
import { TPoint } from '@GFTypes/Point';
import { TSymbolSpineAsset } from '@GFTypes/SymbolAsset';
import EventsCenter from '@Managers/Events/EventsCenter';
import { ESymbolEvents } from '@Managers/Events/SymbolEvents';
import { Container, DestroyOptions } from 'pixi.js';
import { GameScene } from '..';
import Reel from './Reel';
import { ESymbolAnimations } from './SymbolAnimations';
import { ESymbolRoles } from './SymbolRoles';

class Symbol extends Container {
    public static readonly BLOCKER_NAME = 'blockWhenSymbolIsBlocked';
    public readonly gameStore: GameStore;
    public scene: GameScene;
    public reel: Reel;
    public reelIndex: number;
    public symbolIndex: number;
    public symbolId: number;
    public symbolSpineConfig: TSymbolSpineAsset;
    public startingPosition: TPoint;
    public symbolAnimation: SpineWrapper;
    public isBlocked: boolean = false;
    public symbolLabels: Map<string, TextContainer> = new Map();
    public isBlurred: boolean = false;
    public isMultiSymbol: boolean;
    private _componentSymbolIds: number[] = [];

    constructor(
        reel: Reel,
        scene: GameScene,
        position: TPoint,
        symbolSpineConfig: TSymbolSpineAsset,
        symbolId: number,
        reelIndex: number,
        symbolIndex: number,
        protected readonly multiSymbols?: IMultiSymbolFeature
    ) {
        super();
        this.scene = scene;
        this.reel = reel;
        this.startingPosition = position;
        this.gameStore = this.scene.game.gameStore;
        this.setSymbolData(symbolId, symbolSpineConfig, reelIndex, symbolIndex);
        this.createSymbolAnimation(scene, symbolSpineConfig);
        this.isMultiSymbol = isMultiSymbol(this.gameStore, symbolId);
        this._componentSymbolIds = this.multiSymbols?.componentSymbolIdsByMultiSymbolIds.get(symbolId) ?? [];
        if (this.isMultiSymbol) this.setMultiSymbolAttachments();
        this.position.set(position.x, position.y);
        const { width, height, baseWidth, baseHeight } = this.scene.game.gameStore.get(EGameStoreKeys.SymbolParameters);
        setContainerScaleToFitSpineSize(this, this.symbolAnimation, { baseWidth, baseHeight, width, height });
    }

    public get visibleSymbolIndex(): number {
        return this.symbolIndex - 1;
    }

    public get componentSymbolIds(): number[] {
        return this.isMultiSymbol ? this._componentSymbolIds : [];
    }

    public get jackpotLevels(): number[] | null {
        if (this.isMultiSymbol) {
            return this._componentSymbolIds.flatMap((symbolId) => getSymbolJackpotLevels(this.gameStore, symbolId));
        }
        return getSymbolJackpotLevels(this.gameStore, this.symbolId);
    }

    private setSymbolData(symbolId: number, symbolSpineConfig: TSymbolSpineAsset, reelIndex: number, symbolIndex: number): void {
        this.symbolId = symbolId;
        this.symbolSpineConfig = symbolSpineConfig;
        this.reelIndex = reelIndex;
        this.symbolIndex = symbolIndex;
        this._componentSymbolIds = this.multiSymbols?.componentSymbolIdsByMultiSymbolIds.get(symbolId) ?? [];
        this.isMultiSymbol = isMultiSymbol(this.gameStore, symbolId);
    }

    private createSymbolAnimation(scene: GameScene, symbolSpineConfig: TSymbolSpineAsset): void {
        this.symbolAnimation = new SpineWrapper(scene, { x: 0, y: 0 }, { ...symbolSpineConfig, atlasKey: undefined, manuallyScaleOnResize: true }, true, true, [
            new SpecialSymbolModifier(this.gameStore, this.symbolId)
        ]);
        this.symbolAnimation.eventManager.playEventAnimation(ESymbolAnimations.DEFAULT);
        this.symbolAnimation.spine.scale = 1;
        this.addChild(this.symbolAnimation.spine);
    }

    public block(): void {
        this.isBlocked = true;
        this.symbolAnimation.eventManager.registerBlocker(Symbol.BLOCKER_NAME, () => this.isBlocked);
    }

    public unBlock(): void {
        this.isBlocked = false;
        this.symbolAnimation.eventManager.unregisterBlocker(Symbol.BLOCKER_NAME);
    }

    public changeSymbol(symbolId: number, symbolSpineConfig: TSymbolSpineAsset, reelIndex: number, symbolIndex: number, pool: SymbolSpinePool): void {
        this.symbolAnimation.eventManager.unregisterBlocker(Symbol.BLOCKER_NAME);
        this.removeSymbolLabels();
        this.removeChild(this.symbolAnimation.spine as any);
        pool.releaseSpine(this.symbolSpineConfig.dataKey, this.symbolAnimation);
        this.setSymbolData(symbolId, symbolSpineConfig, reelIndex, symbolIndex);
        this.symbolAnimation = pool.getSpine(symbolSpineConfig.dataKey);
        this.symbolAnimation.eventManager.registerBlocker(Symbol.BLOCKER_NAME, () => this.isBlocked);
        if (symbolSpineConfig.skinName) this.symbolAnimation.spine.skeleton.setSkin(symbolSpineConfig.skinName);
        this.symbolAnimation.spine.position.set(0, 0);
        this.symbolAnimation.spine.scale.set(1);
        this.symbolAnimation.spine.visible = true;
        this.symbolAnimation.eventManager.playEventAnimation(ESymbolAnimations.DEFAULT);
        if (this.isMultiSymbol) this.setMultiSymbolAttachments();
        this.addChild(this.symbolAnimation.spine);
        this.removeBlur();
        const { width, height, baseWidth, baseHeight } = this.scene.game.gameStore.get(EGameStoreKeys.SymbolParameters);
        setContainerScaleToFitSpineSize(this, this.symbolAnimation, { baseWidth, baseHeight, width, height });
    }

    public stopAnimation(): void {
        this.symbolAnimation.eventManager.playEventAnimation(ESymbolAnimations.DEFAULT);
    }

    public isWild(): boolean {
        return getSymbolRoles(this.gameStore, this.symbolId).includes(ESymbolRoles.WILD);
    }

    public playShortAnimation(): void {
        this.symbolAnimation.eventManager.playEventAnimation(
            this.isMultiSymbol ? this.getMultiSymbolAnimationVariant(ESymbolAnimations.SHORT) : ESymbolAnimations.SHORT
        );
        const count = this.scene.game.soundManager.symbolSoundsManager.getNextShortAnimSoundCount(this.symbolId);
        EventsCenter.emit(ESymbolEvents.SYMBOL_$_SHORT_ANIM_$, {
            eventIds: [this.symbolId, count],
            modifiers: this.symbolAnimation.eventManager.localEventModifiers
        });
    }

    public playWildAnimation(): void {
        this.symbolAnimation.play(ESymbolAnimations.WILD, false);
    }

    public applyBlur(): void {
        if (!this.isBlurred) {
            this.isBlurred = true;
            this.symbolAnimation.eventManager.playEventAnimation(ESymbolAnimations.BLUR);
        }
    }

    public removeBlur(): void {
        if (this.isBlurred) {
            this.isBlurred = false;
            this.symbolAnimation.eventManager.playEventAnimation(ESymbolAnimations.DEFAULT);
        }
    }

    public getAnimationVariant(animation: ESymbolAnimations, { onlyInterchangeableSymbols, multiplier }: CycleStep): string {
        if (this.isMultiSymbol) {
            return this.getMultiSymbolAnimationVariant(animation);
        }

        if (onlyInterchangeableSymbols && animation === ESymbolAnimations.WIN) {
            const interchangeVariant = this.symbolAnimation.eventManager.getAnimationNameForGameEvent(ESymbolAnimations.INTERCHANGEABLE_SYMBOL_WIN);
            return interchangeVariant ?? animation;
        }

        if (multiplier && multiplier > 1) {
            const animationWithMultiplier = this.symbolAnimation.eventManager.getAnimationNameForGameEvent(`${animation}_X_${multiplier}`);
            return animationWithMultiplier ?? animation;
        }

        return animation;
    }

    public getMultiSymbolAnimationVariant(animation: ESymbolAnimations): string {
        const nonBlankIndices = this.componentSymbolIds
            .map((componentSymbolId, index) => (componentSymbolId !== 0 ? index + 1 : null))
            .filter((index): index is number => index !== null);

        if (nonBlankIndices.length === 0 || nonBlankIndices.length === this.componentSymbolIds.length) {
            return animation;
        }

        const variant = `${animation}_${nonBlankIndices.at(-1)}`;
        return this.symbolAnimation.eventManager.hasGameEventAnimation(variant) ? variant : animation;
    }

    private setMultiSymbolAttachments(): void {
        if (!this.multiSymbols) return;

        const {
            symbolAttachmentPrefix,
            symbolSlotNamePrefix,
            symbolAttachmentNamesBySymbolId,
            labelSlotNamePrefix,
            labelAttachmentPrefix,
            labelAttachmentSuffix,
            labelAttachmentNamesBySymbolId,
            blankName
        } = this.multiSymbols.config;

        this.componentSymbolIds.forEach((componentSymbolId, index) => {
            const symbolSuffix = componentSymbolId === 0 ? blankName : symbolAttachmentNamesBySymbolId?.[componentSymbolId];

            if (symbolSuffix) {
                const attachmentName = `${symbolAttachmentPrefix}${symbolSuffix}`;
                const slotName = `${symbolSlotNamePrefix}${index + 1}`;
                this.symbolAnimation.spine.skeleton.setAttachment(slotName, attachmentName);
            } else {
                console.warn(`[Symbol] No attachment name configured for component symbolId ${componentSymbolId}`);
            }

            const labelName = labelAttachmentNamesBySymbolId?.[componentSymbolId];
            if (!labelName) return;

            const labelAttachmentName = `${labelAttachmentPrefix}${labelName}${labelAttachmentSuffix}`;
            const labelSlotName = `${labelSlotNamePrefix}${index + 1}`;
            this.symbolAnimation.spine.skeleton.setAttachment(labelSlotName, labelAttachmentName);
        });
    }

    public setSymbolLabel(label: string, placeholderSlotName: string, textConfig?: Partial<TextContainerConfig>): void {
        this.removeSymbolLabel(placeholderSlotName);

        const textContainer = new TextContainer(label, this.scene, textConfig);
        this.symbolLabels.set(placeholderSlotName, textContainer);
        this.addChild(textContainer);
        this.symbolAnimation.slotManager.assignObjectToSlot(placeholderSlotName, textContainer);
    }

    public removeSymbolLabel(slotName: string): void {
        const textContainer = this.symbolLabels.get(slotName);
        if (!textContainer) return;

        if (this.symbolAnimation.spine.skeleton.findSlot(slotName)) {
            this.symbolAnimation.slotManager.unassignObjectFromSlot(slotName);
        }
        this.removeChild(textContainer);
        textContainer.destroy();
        this.symbolLabels.delete(slotName);
    }

    public removeSymbolLabels(): void {
        [...this.symbolLabels.keys()].forEach((slotName) => this.removeSymbolLabel(slotName));
    }

    public destroy(options?: DestroyOptions): void {
        this.symbolAnimation.eventManager.unregisterBlocker(Symbol.BLOCKER_NAME);
        this.removeSymbolLabels();
        this.removeChild(this.symbolAnimation.spine);
        this.scene.reels.symbolSpinePool.releaseSpine(this.symbolSpineConfig.dataKey, this.symbolAnimation);
        super.destroy(options);
    }
}

//!  Work around a Babel transform crash with inline `export default class Symbol extends Container`
export default Symbol;
